// Client-side validation schemas and utilities
// This complements our server-side validation in Cloud Functions

// Validation patterns
const PATTERNS = {
  // YouTube URL validation
  youtubeUrl:
    /^https?:\/\/(www\.)?(youtube\.com|music\.youtube\.com|youtu\.be)\/.+/,

  // Safe text content (no HTML/script tags) - Basic version
  safeText: /^[a-zA-Z0-9\s\-_.,!?'"()&:;[\]{}\\/\\|@#$%^&*+=~]+$/,

  // More permissive text for song posts and descriptions - supports international characters
  songText: /^[\p{L}\p{N}\p{P}\p{Z}\p{S}]+$/u,

  // International text for user profiles and comments - supports all languages
  internationalText: /^[\p{L}\p{N}\p{P}\p{Z}\p{S}]+$/u,

  // Email validation
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,

  // User ID validation (Firebase Auth UID format)
  userId: /^[a-zA-Z0-9_-]{20,}$/,

  // Image URL validation
  imageUrl: /^https?:\/\/.+\.(jpg|jpeg|png|webp|gif)$/,
};

// Validation rules for different data types
export const VALIDATION_RULES = {
  song: {
    songName: {
      required: true,
      minLength: 1,
      maxLength: 200,
      message: 'Song name needs at least one visible character (max 200)',
    },
    songArtist: {
      required: false,
      maxLength: 100,
      message: 'Artist name must be under 100 characters',
    },
    URL: {
      required: true,
      pattern: PATTERNS.youtubeUrl,
      maxLength: 500,
      message: 'Please enter a valid YouTube URL',
    },
    description: {
      required: false,
      maxLength: 3000,
      // Remove pattern validation for descriptions to allow natural language
      message: 'Description must be under 3000 characters',
    },
    genres: {
      required: false,
      maxItems: 3,
      maxLength: 120,
      message: 'Genres must be under 120 characters each, maximum 3 genres',
    },
  },

  profile: {
    displayName: {
      required: true,
      minLength: 1,
      maxLength: 50,
      pattern: PATTERNS.internationalText,
      message: 'Display name must be 1-50 characters',
    },
    website: {
      required: false,
      maxLength: 100,
      pattern: /^https?:\/\/.+|^$/,
      message:
        'Please enter a valid website URL starting with http:// or https://',
    },
    instagram: {
      required: false,
      maxLength: 100,
      pattern: /^[a-zA-Z0-9._]+$|^$/,
      message:
        'Instagram handle should only contain letters, numbers, dots, and underscores',
    },
    twitter: {
      required: false,
      maxLength: 100,
      pattern: /^[a-zA-Z0-9_]+$|^$/,
      message:
        'Twitter handle should only contain letters, numbers, and underscores',
    },
    bio: {
      required: false,
      maxLength: 1000,
      pattern: PATTERNS.internationalText,
      message: 'Bio must be under 1000 characters',
    },
  },

  comment: {
    text: {
      required: true,
      minLength: 1,
      maxLength: 1000,
      pattern: PATTERNS.internationalText,
      message: 'Comment must be 1-1000 characters',
    },
  },

  user: {
    displayName: {
      required: true,
      minLength: 1,
      maxLength: 50,
      pattern: PATTERNS.internationalText,
      message: 'Display name must be 1-50 characters',
    },
    email: {
      required: true,
      pattern: PATTERNS.email,
      maxLength: 100,
      message: 'Please enter a valid email address',
    },
  },
};

// Main validation function
export function validateField(value, rules) {
  const errors = [];

  // Required field check
  if (rules.required && (!value || value.toString().trim() === '')) {
    errors.push('This field is required');
    return errors;
  }

  // Skip validation for optional empty fields
  if (!rules.required && (!value || value.toString().trim() === '')) {
    return errors;
  }

  const stringValue = value.toString().trim();

  // Length validation
  if (rules.minLength && stringValue.length < rules.minLength) {
    errors.push(`Must be at least ${rules.minLength} characters long`);
  }

  if (rules.maxLength && stringValue.length > rules.maxLength) {
    errors.push(`Must be no more than ${rules.maxLength} characters long`);
  }

  // Pattern validation
  if (rules.pattern && !rules.pattern.test(stringValue)) {
    errors.push(rules.message || 'Contains invalid characters or format');
  }

  // Array validation
  if (Array.isArray(value)) {
    if (rules.maxItems && value.length > rules.maxItems) {
      errors.push(`Cannot have more than ${rules.maxItems} items`);
    }

    if (rules.maxLength) {
      for (let i = 0; i < value.length; i++) {
        if (value[i].length > rules.maxLength) {
          errors.push(
            `Item ${i + 1} exceeds maximum length of ${rules.maxLength}`
          );
        }
      }
    }
  }

  return errors;
}

// Validate entire form data
export function validateForm(data, schema) {
  const errors = {};
  let isValid = true;

  for (const [fieldName, fieldRules] of Object.entries(schema)) {
    const fieldErrors = validateField(data[fieldName], fieldRules);
    if (fieldErrors.length > 0) {
      errors[fieldName] = fieldErrors;
      isValid = false;
    }
  }

  return {
    isValid,
    errors,
  };
}

// Sanitization utilities
export const sanitizers = {
  // Remove potentially dangerous HTML/script tags
  html: (str) => {
    if (typeof str !== 'string') return str;
    return str
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
      .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
      .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '')
      .trim();
  },

  // Sanitize text content
  text: (str) => {
    if (typeof str !== 'string') return str;
    return str
      .replace(/[<>]/g, '') // Remove angle brackets
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '')
      .trim();
  },

  // Sanitize URLs
  url: (str) => {
    if (typeof str !== 'string') return str;
    const url = str.trim();

    // Only allow http/https URLs
    if (!url.match(/^https?:\/\//)) {
      throw new Error('Invalid URL protocol');
    }

    // Prevent open redirects to external domains
    const allowedDomains = [
      'youtube.com',
      'www.youtube.com',
      'music.youtube.com',
      'youtu.be',
      'gs-7b1e1.web.app',
      'goodsong.club',
    ];

    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();

      if (
        !allowedDomains.some(
          (domain) => hostname === domain || hostname.endsWith('.' + domain)
        )
      ) {
        throw new Error('URL domain not allowed');
      }

      return url;
    } catch (error) {
      throw new Error('Invalid URL format');
    }
  },
};

// Error message formatting
export function formatErrors(errors) {
  if (!errors || errors.length === 0) return '';

  if (Array.isArray(errors)) {
    return errors.join('; ');
  }

  if (typeof errors === 'object') {
    return Object.values(errors).flat().filter(Boolean).join('; ');
  }

  return errors.toString();
}

// Export patterns for external use
export { PATTERNS };
